import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import excelIcon from "../../assets/img/icons/excel-green.svg";
import plusIcon from "../../assets/img/icons/plus_white.svg";
import AppOnboardingIndividualPage from "../../components/AppOnboarding/AppOnboardingIndividualPage";
import ButtonTextLoader from "../../components/Loaders/ButtonTextLoader";
import BottomPagination from "../../components/Pagination/BottomPagination/BottomPagination";
import TopPagination from "../../components/Pagination/TopPagination/TopPagination";
import PricingPlan from "../../components/PricingPlan";
import { useScrollShadow } from "../../customHooks/useScrollShadow";
import { setPathName } from "../../filterReducer";
import {
  ButtonIcon,
  ColoredButtonWithIcon,
  WhiteButtonWithIcon,
} from "../../styledComponents/buttons";
import {
  CommonFlex,
  CommonGrid,
  Container,
  ContainerNew,
  HeaderContainer,
  PageTitle,
} from "../../styledComponents/common";
import { StickyFilterContainer } from "../../styledComponents/timesheet";
import DeleteJobModal from "./DeleteJobModal";
import EditJobPopup from "./EditJobPopup/EditJobPopupContainer";
import JobFilter from "./JobFilter";
import JobList from "./JobList";

const Jobs = ({
  selectedOrganization,
  getOrganizationMembersShortList,
  orgMembersShortList,
  loadingTaskAssigneeId,
  history,
  //jobs
  getJobList,
  jobList,
  isLoadingJobs,
  totaljobsCount,
  pageSizeJob,
  reloadJobList,
  //workorder short
  getWorkOrderShortList,
  workOrderShortList,
  //delete job
  deleteJob,
  jobDeleteLoading,
  deletingJobId,
  filters,
  exportJobReport,
  isJobExportLoading,
  createOrUpdateJobIsLoading,
  updatedJob,
  appOnboardingFeatures,
  appOnboardingIsFeaturesLoading,
  getOnboardingFeatures,
  appOnboardingState,
  dismissOnboardingFeature,
  dismissFeatureLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showShadow = useScrollShadow(60);
  const role = selectedOrganization?.role;
  const showOnboarding =
    !appOnboardingIsFeaturesLoading &&
    !appOnboardingFeatures?.has_job &&
    !appOnboardingFeatures?.dismissed_videos?.includes("job") &&
    (role === "owner" || role === "admin");

  // Dynamic paths and labels based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const basePath = isOperationType ? "/user/jobs" : "/user/tasks";
  const createPath = isOperationType ? "/user/create-task" : "/user/create-job";
  const allItemsLabel = isOperationType ? t("all_tasks") : t("all_jobs");
  const createItemLabel = isOperationType ? t("create_task") : t("create_job");
  const itemNameLabel = isOperationType ? t("tasks") : t("jobs");

  const [selectedWorkOrder, setSelectedWorkOrder] = useState(() => {
    const currentOrgType = selectedOrganization?.type || "operation";
    return (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.selectedWorkOrder
      ? filters?.selectedWorkOrder
      : {
          label: `(${t(
            currentOrgType === "service" ? "All Jobs" : "All Projects",
          )})`,
          value: null,
        };
  });
  const [selectedMember, setSelectedMember] = useState(
    (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.selectedMember
      ? filters?.selectedMember
      : "",
  );
  const [taskStatus, setTaskStatus] = useState(
    (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.taskStatus
      ? filters?.taskStatus
      : {
          label: `(${t("All except later")})`,
          value: "all",
        },
  );
  const [searchTerm, setSearchTerm] = useState(
    (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.currentSearchedTerm
      ? filters?.currentSearchedTerm
      : "",
  );
  const [currentSearchedTerm, setCurrentSearchedTerm] = useState(
    (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.currentSearchedTerm
      ? filters?.currentSearchedTerm
      : "",
  );
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [jobIdDelete, setJobIdDelete] = useState(null);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [pricingPopup, setPricingPopup] = useState(false);
  const [isEditJobPopupOpen, setIsEditJobPopupOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [updatedJobId, setUpdatedJobId] = useState(null);

  // pagination
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const plan = "free";

  // SingleDate -> DateRange
  const [startDate, setStartDate] = useState(
    (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.startDate
      ? filters?.startDate
      : moment().startOf("week").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    (filters?.pathname === "/user/tasks" ||
      filters?.pathname === "/user/jobs") &&
      filters?.endDate
      ? filters?.endDate
      : moment().endOf("week").format("YYYY-MM-DD"),
  );
  const [dateLabel, setDateLabel] = useState("");
  const [dateChanged, setDateChanged] = useState(false);

  // SingleDate -> DateRange
  const selectLastWeek = () => {
    setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
    setEndDate(moment().format("YYYY-MM-DD"));
    setDateChanged(!dateChanged);
  };
  const selectLastMonth = () => {
    setStartDate(moment().subtract(29, "days").format("YYYY-MM-DD"));
    setEndDate(moment().format("YYYY-MM-DD"));
    setDateChanged(!dateChanged);
  };
  const selectNextWeek = () => {
    setStartDate(moment().add(1, "days").format("YYYY-MM-DD"));
    setEndDate(moment().add(7, "days").format("YYYY-MM-DD"));
    setDateChanged(!dateChanged);
  };
  const changeDateRange = dates => {
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
  };
  const getDateLabel = () => {
    const diff = Math.ceil(
      moment(endDate).diff(moment(startDate), "days", true),
    );
    if (
      diff === 0 &&
      moment().format("YYYY-MM-DD") === moment(startDate).format("YYYY-MM-DD")
    ) {
      return "Today selected";
    }
    return diff + 1 + " day" + (diff > 0 ? "s" : "") + " " + "selected";
  };
  const singleDateChange = direction => {
    if (direction === "right") {
      setStartDate(moment(endDate).add(1, "days"));
      setEndDate(moment(endDate).add(1, "days"));
    } else {
      setStartDate(moment(startDate).subtract(1, "days"));
      setEndDate(moment(startDate).subtract(1, "days"));
    }
    setDateChanged(!dateChanged);
  };
  const selectToday = () => {
    setStartDate(moment());
    setEndDate(moment());
    setDateChanged(!dateChanged);
  };

  useEffect(() => {
    if (startDate) {
      if (!endDate) {
        setEndDate(startDate);
      }

      const maxEndDate = moment(startDate).add(3, "months");
      if (
        endDate &&
        moment(endDate).diff(moment(startDate), "months", true) > 3
      ) {
        setEndDate(maxEndDate);
      }

      if (startDate && endDate) {
        setDateLabel(getDateLabel());
      }
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (
      // selectedOrganization?.plan?.name === "Standard" ||
      selectedOrganization?.plan?.nick_name === "standard-free"
    ) {
      pricingPopupToggle();
    }
  }, [selectedOrganization]);

  // Fetch onboarding features status
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      getOnboardingFeatures(selectedOrganization.id);
    }
  }, [selectedOrganization?.id, getOnboardingFeatures]);

  const handleSearchByName = () => {
    if (searchTerm && searchTerm.trim()) {
      setCurrentSearchedTerm(searchTerm);
    } else {
      setCurrentSearchedTerm("");
    }
    setCurrentPageNumber(1);
  };
  const handleClearSearch = () => {
    setCurrentPageNumber(1);
    setSearchTerm("");
    setCurrentSearchedTerm("");
  };

  const handlePageChange = page => {
    setCurrentPageNumber(page);
  };

  const selectWorkOrder = e => {
    setCurrentPageNumber(1);
    // setSearchTerm("");
    // setCurrentSearchedTerm("");
    // setSelectedMember("");
    setSelectedWorkOrder(e);
  };

  const selectMember = person => {
    if (person.id) {
      setSelectedMember(person);
      // setSearchTerm("");
      // setCurrentSearchedTerm("");
      setCurrentPageNumber(1);
    }
  };

  const clearMember = () => {
    setCurrentPageNumber(1);
    setSelectedMember("");
  };

  const onSearchTermChange = e => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  const onJobUpdateChange = value => {
    if (value) {
      // Set a timestamp to trigger refetch with same filters
      setUpdatedJobId(Date.now());
    }
  };

  const callGetJobsList = () => {
    const payload = {
      paginate: true,
      page: currentPageNumber,
    };

    if (selectedWorkOrder && selectedWorkOrder.value) {
      payload.work_order_id = selectedWorkOrder.value;
    }

    if (selectedMember && selectedMember.id) {
      payload.assignee_id = selectedMember.id;
    }

    if (taskStatus && taskStatus.value !== "all") {
      payload.status = taskStatus.value;
    }

    if (taskStatus && taskStatus.value !== "unscheduled") {
      payload.start_date = moment(startDate).format("YYYY-MM-DD");
      payload.end_date = moment(endDate).format("YYYY-MM-DD");
    }
    if (currentSearchedTerm && currentSearchedTerm.length > 0) {
      payload.job_name = currentSearchedTerm.toLowerCase().trim();
    }
    if (selectedOrganization && selectedOrganization.id) {
      payload.organization_id = selectedOrganization.id;
      getJobList(payload);
    }
  };
  useEffect(() => {
    if (selectedOrganization?.id && reloadJobList) {
      callGetJobsList();
    }
  }, [reloadJobList]);

  useEffect(() => {
    if (selectedOrganization?.id) {
      callGetJobsList();
    }
  }, [
    currentPageNumber,
    selectedOrganization,
    selectedWorkOrder,
    selectedMember,
    taskStatus,
    dateChanged,
    currentSearchedTerm,
  ]);
  useEffect(() => {
    dispatch(
      setPathName({
        pathname: location.pathname,
        selectedWorkOrder,
        selectedMember,
        taskStatus,
        startDate,
        endDate,
        currentSearchedTerm,
      }),
    );
  }, [
    selectedWorkOrder,
    selectedMember,
    taskStatus,
    dateChanged,
    currentSearchedTerm,
  ]);
  const handleStatusChange = e => {
    setCurrentPageNumber(1);
    // setSearchTerm("");
    // setCurrentSearchedTerm("");
    setTaskStatus(e);
  };

  const handleDateChange = () => {
    setCurrentPageNumber(1);
  };

  useEffect(() => {
    handleDateChange();
  }, [dateChanged]);

  useEffect(() => {
    if (updatedJobId && selectedOrganization && selectedOrganization.id) {
      callGetJobsList();
    }
  }, [updatedJobId]);

  const toggleDeleteModal = (jobId, workOrderId) => {
    setIsOpen(!isOpen);
    if (jobId && workOrderId) {
      setJobIdDelete(jobId);
      setSelectedWorkOrderId(workOrderId);
    }
  };

  const toggleEditJobPopup = () => {
    setIsEditJobPopupOpen(!isEditJobPopupOpen);
  };

  const pricingPopupToggle = () => {
    setPricingPopup(!pricingPopup);
  };

  const handleJobDelete = useDebouncedCallback(() => {
    if (
      selectedOrganization &&
      selectedOrganization.id &&
      jobIdDelete &&
      selectedWorkOrderId
    ) {
      const payload = {
        organization_id: selectedOrganization.id,
        workOrder_id: selectedWorkOrderId,
        job_id: jobIdDelete,
        orgType: orgType,
        reloadJobList:
          Math.ceil(totaljobsCount / pageSizeJob) !== currentPageNumber,
      };

      deleteJob(payload);
      toggleDeleteModal();
    }
  }, 500);

  const handleExportJobReport = () => {
    const payload = {
      selectedOrganization: selectedOrganization,
    };
    if (selectedWorkOrder && selectedWorkOrder.value) {
      payload.work_order_id = selectedWorkOrder.value;
    }

    if (selectedMember && selectedMember.id) {
      payload.assignee_id = selectedMember.id;
    }

    if (taskStatus && taskStatus.value !== "all") {
      payload.status = taskStatus.value;
    }

    if (taskStatus && taskStatus.value !== "unscheduled") {
      payload.start_date = moment(startDate).format("YYYY-MM-DD");
      payload.end_date = moment(endDate).format("YYYY-MM-DD");
    }
    if (currentSearchedTerm && currentSearchedTerm.length > 0) {
      payload.job_name = currentSearchedTerm.toLowerCase().trim();
    }
    if (selectedOrganization && selectedOrganization.id) {
      payload.organization_id = selectedOrganization.id;
      exportJobReport(payload);
    }
  };

  return (
    <div className="content">
      <DeleteJobModal
        isOpen={isOpen}
        toggle={() => toggleDeleteModal()}
        handleDelete={handleJobDelete}
        isLoading={jobDeleteLoading}
      />
      <PricingPlan
        isOpen={pricingPopup}
        toggle={pricingPopupToggle}
        popupText="You don't have permission to this feature."
        popupTitle="Upgrade to Elite to access this feature"
        history={history}
        backRoute="/user/dashboard"
        onlyElite
      />
      <EditJobPopup
        isOpen={isEditJobPopupOpen}
        toggle={toggleEditJobPopup}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        onUpdateChange={onJobUpdateChange}
      />
      <ContainerNew padding="20px" marginTop="0px">
        {appOnboardingIsFeaturesLoading ? null : showOnboarding ? (
          <AppOnboardingIndividualPage
            history={history}
            selectedOrganization={selectedOrganization}
            page="create-job"
            appOnboardingState={appOnboardingState}
            dismissOnboardingFeature={dismissOnboardingFeature}
            dismissFeatureLoading={dismissFeatureLoading}
          />
        ) : (
          <>
            <CommonGrid alignItem="center">
              <PageTitle>{allItemsLabel}</PageTitle>
              {selectedOrganization &&
              selectedOrganization.role &&
              (selectedOrganization.role === "admin" ||
                selectedOrganization.role === "owner") ? (
                <CommonFlex disableResponsive={true} gap="12px">
                  <WhiteButtonWithIcon
                    disabled={isJobExportLoading || jobList.length === 0}
                    onClick={() => {
                      if (isJobExportLoading || jobList.length === 0) return;
                      handleExportJobReport();
                    }}
                    color="#19AC65"
                    borderColor="#19AC65"
                    hoverBorderColor="#129652"
                    style={{
                      ...(isJobExportLoading || jobList.length === 0
                        ? { cursor: "not-allowed" }
                        : {}),
                      height: "40px",
                    }}
                  >
                    {isJobExportLoading ? (
                      <ButtonTextLoader
                        color="#19AC65"
                        loadingText=" "
                        size={6}
                      />
                    ) : (
                      <>
                        <ButtonIcon src={excelIcon} size="26px" />
                        {t("export_report")}
                      </>
                    )}
                  </WhiteButtonWithIcon>
                  <ColoredButtonWithIcon
                    onClick={() => history.push(createPath)}
                  >
                    <ButtonIcon src={plusIcon} alt="" />
                    {createItemLabel}
                  </ColoredButtonWithIcon>
                </CommonFlex>
              ) : (
                <> </>
              )}
            </CommonGrid>
            <StickyFilterContainer
              style={{
                width: "calc(100% + 40px)",
                marginLeft: "-20px",
                marginRight: "-20px",
                paddingLeft: "24px",
                paddingRight: "24px",
                marginBottom: "0px",
              }}
              showShadow={showShadow}
            >
              <HeaderContainer margin="0px" columns="1fr">
                <JobFilter
                  selectedOrganization={
                    selectedOrganization && selectedOrganization.id
                      ? selectedOrganization
                      : ""
                  }
                  getOrganizationMembersList={getOrganizationMembersShortList}
                  organizationMembersList={orgMembersShortList}
                  //WorkOrder short list
                  workOrderShortList={workOrderShortList}
                  getWorkOrderShortList={getWorkOrderShortList}
                  selectedWorkOrder={selectedWorkOrder}
                  setSelectedWorkOrder={setSelectedWorkOrder}
                  selectWorkOrder={selectWorkOrder}
                  selectedMember={selectedMember}
                  selectMember={selectMember}
                  clearMember={clearMember}
                  searchTerm={searchTerm}
                  onSearchTermChange={onSearchTermChange}
                  taskStatus={taskStatus}
                  handleStatusChange={handleStatusChange}
                  handleSearchByName={handleSearchByName}
                  handleClearSearch={handleClearSearch}
                  startDate={startDate}
                  endDate={endDate}
                  dateLabel={dateLabel}
                  singleDateChange={singleDateChange}
                  selectToday={selectToday}
                  changeDateRange={changeDateRange}
                  onDateSelect={() => {
                    setDateChanged(!dateChanged);
                  }}
                  selectLastWeek={selectLastWeek}
                  selectLastMonth={selectLastMonth}
                  selectNextWeek={selectNextWeek}
                  totaljobsCount={totaljobsCount}
                  isLoadingJobs={isLoadingJobs}
                />
              </HeaderContainer>
            </StickyFilterContainer>

            {totaljobsCount && totaljobsCount > 0 && pageSizeJob ? (
              <TopPagination
                itemName={itemNameLabel}
                totalCount={totaljobsCount}
                currentPage={currentPageNumber}
                onPageChange={page => handlePageChange(page)}
                pageSize={pageSizeJob}
              />
            ) : null}

            <JobList
              selectedOrganization={selectedOrganization}
              loadingTaskAssigneeId={loadingTaskAssigneeId}
              expandedTaskId={expandedTaskId}
              setExpandedTaskId={setExpandedTaskId}
              toggleDeleteModal={toggleDeleteModal}
              history={history}
              jobList={jobList}
              isLoading={isLoadingJobs}
              taskStatus={taskStatus}
              setIsEditJobPopupOpen={setIsEditJobPopupOpen}
              setSelectedJob={setSelectedJob}
            />

            {totaljobsCount && totaljobsCount > 0 && pageSizeJob ? (
              <BottomPagination
                totalCount={totaljobsCount}
                currentPage={currentPageNumber}
                onPageChange={page => handlePageChange(page)}
                pageSize={pageSizeJob}
              />
            ) : null}
          </>
        )}
      </ContainerNew>
    </div>
  );
};

export default Jobs;
